import type {
  BetType,
  PoolBetType,
  SingleRaceBetType,
} from '@trmediaab/zebra-types/trais/trot';

export const atgPoolBetTypes: PoolBetType[] = [
  'V75',
  'V86',
  'GS75',
  'V64',
  'V65',
  'V5',
  'V4',
  'V3',
  'DD',
  'LD',
];

export const atgSingleRaceBetTypes: SingleRaceBetType[] = [
  'T',
  'T7',
  'TV',
  'V',
  'P',
  'K',
];

export const atgBetTypes: BetType[] = [
  ...atgPoolBetTypes,
  ...atgSingleRaceBetTypes,
];

export const betTypeDomesticTexts: Record<BetType, string> = {
  V86: 'V86',
  V75: 'V75',
  GS75: 'GS75',
  V64: 'V64',
  V65: 'V65',
  V5: 'V5',
  V4: 'V4',
  V3: 'V3',
  DD: 'Dagens Dubbel',
  LD: 'Lunch Dubbel',
  T: 'Trio',
  T7: 'Top 7',
  TV: 'Tvilling',
  V: 'Vinnare',
  P: 'Plats',
  K: 'Komb',
};

export const betTypePayoutPercentages: Record<BetType, number> = {
  V86: 65,
  V75: 65,
  GS75: 65,
  V64: 65,
  V65: 65,
  V5: 75,
  V4: 75,
  V3: 65,
  DD: 75,
  LD: 75,
  T: 75,
  T7: 65,
  TV: 85,
  V: 85,
  P: 85,
  K: 85,
};

export function isSingleRaceBetType(
  betType: unknown,
): betType is SingleRaceBetType {
  return atgSingleRaceBetTypes.includes(betType as SingleRaceBetType);
}

export function isPoolBetType(betType: unknown): betType is PoolBetType {
  return atgPoolBetTypes.includes(betType as PoolBetType);
}

// Fill a map with priorities based on order for the sort function below.
// Lower index means higher priority.
const priorityMap = new Map<BetType, number>();
const priorityOrder: BetType[] = [
  'V75',
  'V86',
  'GS75',
  'V64',
  'V65',
  'V5',
  'V4',
  'V3',
  'T',
  'DD',
  'LD',
];

priorityOrder.forEach((betType, index) => {
  priorityMap.set(betType, index);
});

/**
 * Sorts a list of bet types by a predefined priority. The list can either
 * be an array of strings or an array of objects with a named property that
 * contains the bet type.
 */
export function sortByBetTypePriority(list: BetType[]): BetType[];
export function sortByBetTypePriority<T extends Record<string, unknown>>(
  list: T[],
  propName: keyof T,
): T[];
export function sortByBetTypePriority<
  T extends BetType | Record<string, unknown>,
>(list: T[], propName?: keyof T): T[] {
  // Define the sorting function
  return [...list].sort((a, b) => {
    // Extract bet types based on whether they are string or object and propName is provided
    const betTypeA = typeof a === 'string' ? a : propName ? a[propName] : null;
    const betTypeB = typeof b === 'string' ? b : propName ? b[propName] : null;

    // Get priority values, use a large number for non-listed types
    const priorityA = priorityMap.get(betTypeA as BetType) ?? 1000;
    const priorityB = priorityMap.get(betTypeB as BetType) ?? 1000;

    return priorityA - priorityB;
  });
}
